import React, { Fragment, useCallback, useEffect, useMemo } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Pane from 'components/Common/Pane'
import useToggle from 'hooks/useToggle'
import BedbankPackageTilePricing from './BedbankPackageTilePricing'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import Group from 'components/utils/Group'
import SolidBoltAltIcon from 'components/Luxkit/Icons/solid/SolidBoltAltIcon'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BedbankCancellationPolicy from './BedbankCancellationPolicy'
import BedbankOfferInclusions from './BedbankOfferInclusions'
import { take } from 'lib/array/arrayUtils'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LineUtensilsIcon from 'components/Luxkit/Icons/line/LineUtensilsIcon'
import LineCheckIcon from 'components/Luxkit/Icons/line/LineCheckIcon'
import WalledContent from 'components/Common/WalledContent'
import useBedbankSelectDates, { useBedbankSelectDatesWithFlights } from 'hooks/OfferPage/useBedbankSelectDates'
import AccommodationRoomTile from '../Accommodation/AccommodationRoomTile'
import AccommodationRoomTileImage from '../Accommodation/AccommodationRoomTileImage'
import AccommodationRoomTileDetails from '../Accommodation/AccommodationRoomTileDetails'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from '../Accommodation/AccommodationRoomTileActions'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import Divider from 'components/Luxkit/Divider'
import AccommodationPackageTile from '../Accommodation/AccommodationPackageTile'
import AccommodationPackageTileContentArea from '../Accommodation/AccommodationPackageTileContentArea'
import AccommodationPackageTileDetails from '../Accommodation/AccommodationPackageTileDetails'
import BedbankPromotionDetails from './BedbankPromotionDetails'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import HighlightMessage from 'components/Common/HighlightMessage/HighlightMessage'
import BedbankMobileOnlyPriceLabel from 'components/Bedbank/BedbankMobileOnlyPriceLabel'

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

const foodInclusions = ['breakfast', 'lunch', 'dinner', 'drinks', 'drink']
const isFoodInclusion = (inclusion: string) => foodInclusions.some(foodInclusion => inclusion.toLowerCase().includes(foodInclusion))

const INITIAL_RATES_COUNT_TO_DISPLAY = 3

interface Props {
  checkinDate?: moment.Moment;
  checkoutDate?: moment.Moment;
  offer: App.BedbankOffer;
  packageView: App.BedbankOfferPackageView;
  rooms: Array<App.Occupants>;
  onBook: (pkg: App.BedbankPackage, rate: App.BedbankRate, withFlights?: boolean, flightPrice?: number) => void;
  flightsEnabled: boolean;
  flightPrice?: number;
  fetchingRates?: boolean;
  isFilterActive: boolean;
  bookedToday?: number;
  isHighlighted?: boolean;
  onViewPackageOptions: () => void;
  flightOrigin?: App.Airport | undefined;
}

function BedbankRoomOption(props: Props) {
  const {
    offer,
    packageView,
    rooms,
    checkinDate,
    checkoutDate,
    fetchingRates,
    flightsEnabled,
    isFilterActive,
    flightPrice,
    bookedToday = 0,
    onBook: onBookProp,
    isHighlighted = false,
    onViewPackageOptions,
    flightOrigin,
  } = props

  const { value: showingMoreRates, toggle: showMoreRates } = useToggle(false)

  const rates = packageView.rates
  const bundleRates = packageView.bundleRates
  const promotions = packageView.promotions
  const hasMobileOnlyPrice = useMemo(() => {
    return rates.some(rate => rate.mobilePromotion)
  }, [rates])

  const isInitialPackageOption = promotions[rates[0]?.id]?.id === rates[0]?.id

  const openDateSelector = useBedbankSelectDatesWithFlights(packageView.package.roomId)
  const onBook = useCallback((rate: App.BedbankRate, withFlights?: boolean) => {
    if (!checkinDate || !checkoutDate) {
      openDateSelector(withFlights)
    } else {
      onBookProp(packageView.package, rate, withFlights, withFlights ? flightPrice : 0)
    }
  }, [checkinDate, checkoutDate, flightPrice, onBookProp, openDateSelector, packageView.package])

  const departureTooEarly = useMemo(() => {
    const minDay = 1
    return !!checkinDate && checkinDate.diff(moment(), 'days') < minDay
  }, [checkinDate])

  const hasPromotions = offer.promotions?.length > 0
  const bookedTodayLabel = bookedToday >= 3 && hasPromotions

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions()
    }
  }, [isHighlighted]) // eslint-disable-line react-hooks/exhaustive-deps

  const canBuyNow = packageView.available && packageView.capacityValid && packageView.show && !fetchingRates

  const ratesToDisplay = useMemo(() => {
    if (canBuyNow) {
      if (showingMoreRates) {
        return rates
      } else {
        return take(rates, INITIAL_RATES_COUNT_TO_DISPLAY)
      }
    }
  }, [canBuyNow, showingMoreRates, rates])

  const validCheckinCheckout = !!(checkinDate && checkoutDate)
  const hotelAvailable = packageView.available
  const canPurchase = rates.length > 0 && packageView.capacityValid && hotelAvailable && !fetchingRates

  const roomTileActionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (fetchingRates) {
      return 'loading'
    }
    if (!canPurchase) {
      if (validCheckinCheckout && packageView.capacityValid) {
        return 'sold-out-on-dates'
      }
      if (hotelAvailable && !packageView.capacityValid) {
        return 'exceeded-capacity'
      }
    }
    if (validCheckinCheckout && !hotelAvailable && packageView.capacityValid) {
      return 'sold-out'
    }
    if (!isFilterActive && rates.length == 0 && checkinDate && checkoutDate) {
      return 'sold-out'
    }
    if (hotelAvailable && !packageView.capacityValid) {
      return 'exceeded-capacity'
    }
    if (hotelAvailable && packageView.capacityValid && !validCheckinCheckout) {
      return 'available-on-dates'
    }
  }, [canPurchase, checkinDate, checkoutDate, fetchingRates, hotelAvailable, isFilterActive, packageView.capacityValid, rates.length, validCheckinCheckout])

  const offerPageDispatch = useOfferPageDispatch()

  const handleAddAnotherUnitClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const handleChangeDatesClick = useBedbankSelectDates(packageView.package.roomId)
  const handleCheckAvailabilityClick = useBedbankSelectDates(packageView.package.roomId)

  return <Pane shadow data-testid="PackageOption-bedbank">
    {isHighlighted && <HighlightMessage message="You were interested in this option" />}
    <ScrollAnchor id={packageView.package.roomId} vOffset={rem(80)} />
    <AccommodationRoomTile>
      <AccommodationRoomTileImage roomPackage={packageView.package} />
      <AccommodationRoomTileDetails
        roomPackage={packageView.package}
        urgencyLabels={<>
          {bookedTodayLabel && <UrgencyLabel variant="critical1">
            <Group direction="horizontal" verticalAlign="center" gap={4}>
              <SolidBoltAltIcon size="XS" />
              Booked {bookedToday} times today
            </Group>
          </UrgencyLabel>}
          {hasMobileOnlyPrice && <BedbankMobileOnlyPriceLabel />}
        </>}
        actions={<CSSBreakpoint min="desktop">
          <TripGuard>
            <BookmarkButton offer={offer} roomTypeId={packageView.package.roomId} />
          </TripGuard>
        </CSSBreakpoint>}
      >
        <AccommodationRoomTileActions
          roomPackage={packageView.package}
          state={roomTileActionsState}
          onAddAnotherUnitClick={handleAddAnotherUnitClick}
          onChangeDatesClick={handleChangeDatesClick}
          onCheckAvailabilityClick={handleCheckAvailabilityClick}
        />
      </AccommodationRoomTileDetails>
    </AccommodationRoomTile>

    {ratesToDisplay && <>
      {ratesToDisplay.map((rate, i) => {
        const bundleRate = bundleRates.find((bundleRate) => bundleRate.id === rate.id) ?? rate
        return <Fragment key={rate.id}>
          <Divider kind="secondary" />
          <AccommodationPackageTile>
            <AccommodationPackageTileContentArea>
              <AccommodationPackageTileDetails
                packageNumber={i + 1}
                promotion={promotions[rate.id]}
                tagLineLabel={<>
                  {rate.corporate && <ProductTypeLabel productType="corporate" />}
                  {promotions[rate.id] && <ProductTypeLabel productType="limited_time_special" />}
                </>}
              >
                {promotions[rate.id] && <BedbankPromotionDetails
                  checkInDate={checkinDate}
                  checkOutDate={checkoutDate}
                  offer={offer}
                  promotion={promotions[rate.id]}
                  rate={rate}
                  showNonCancellation={!isInitialPackageOption}
                />}
                {!promotions[rate.id] && <BedbankCancellationPolicy
                  rate={rate}
                  offer={offer}
                  showNonCancellation
                />}
              </AccommodationPackageTileDetails>
              <div>
                {promotions[rate.id] && <BedbankOfferInclusions
                  content={promotions[rate.id].rateInclusionsLong}
                  inclusions={promotions[rate.id].inclusions}
                  maxHeight={180}
                />}
                {!promotions[rate.id] && <VerticalSpacer gap={4}>
                  {take(rate.facilities, 30).concat(rate.promotions).map(inclusion =>
                    <BodyTextBlock key={inclusion} variant="medium" startIcon={ isFoodInclusion(inclusion) ? <LineUtensilsIcon /> : <LineCheckIcon />}>
                      {inclusion}
                    </BodyTextBlock>,
                  )}
                </VerticalSpacer>}
              </div>
            </AccommodationPackageTileContentArea>
            <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing}>
              <BedbankPackageTilePricing
                promotions={promotions[rate.id]}
                rate={rate}
                bundleRate={bundleRate}
                rooms={rooms}
                offer={offer}
                checkIn={checkinDate}
                flightPrice={flightPrice}
                flightsEnabled={flightsEnabled}
                departureTooEarly={departureTooEarly}
                onBookNow={onBook}
                flightOrigin={flightOrigin}
              />
            </WalledContent>
          </AccommodationPackageTile>
        </Fragment>
      })}
      {rates.length > INITIAL_RATES_COUNT_TO_DISPLAY && <ShowMoreContent variant="medium" align="center">
        <SmallToggleButton
          on={showingMoreRates}
          onClick={showMoreRates}
        >
          {showingMoreRates ? 'Show less options' : 'Show all options'}
        </SmallToggleButton>
      </ShowMoreContent>}
    </>}
  </Pane>
}

export default BedbankRoomOption
